<template>
  <div class="monthly-report-view-container">
    <!-- 权限检查和加载状态 -->
    <div v-if="loading" class="loading-container">
      <i class="el-icon-loading"></i>
      <p>正在加载月报数据...</p>
    </div>
    
    <!-- 非领队提示 -->
    <div v-else-if="!isLeader" class="no-permission">
      <i class="el-icon-warning"></i>
      <h3>访问受限</h3>
      <p>您不是批次领队，无权限查看月报状态</p>
      <el-button type="primary" @click="goBack">返回</el-button>
    </div>
    
    <!-- 月报查看界面 -->
    <div v-else class="report-view-management">
      <!-- 页面头部 -->
      <div class="page-header">
        <div class="header-content">
          <h2><i class="el-icon-document"></i> 学生月报查看</h2>
          <p class="batch-info">批次：{{ batchInfo.batch_name }}</p>
        </div>
        <div class="header-actions">
          <el-button type="primary" icon="el-icon-back" @click="goBack">返回周报管理</el-button>
        </div>
      </div>

      <!-- 筛选和统计区域 -->
      <el-card shadow="hover" class="filter-card">
        <div class="filter-section">
          <div class="filter-controls">
            <el-date-picker
              v-model="selectedMonth"
              type="month"
              placeholder="选择月份"
              format="yyyy年MM月"
              value-format="yyyy-MM"
              @change="loadMonthlyReports"
              :picker-options="pickerOptions">
            </el-date-picker>
            <el-button type="primary" icon="el-icon-refresh" @click="loadMonthlyReports">刷新</el-button>
            <el-dropdown 
              @command="handleBatchReminderCommand"
              :disabled="pendingCount === 0"
              :loading="isSendingReminders">
              <el-button 
                type="success" 
                icon="el-icon-message"
                :disabled="pendingCount === 0"
                :loading="isSendingReminders">
                一键发送提醒<i class="el-icon-arrow-down el-icon--right"></i>
              </el-button>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item command="email">
                  <i class="el-icon-message"></i> 批量邮件提醒
                </el-dropdown-item>
                <el-dropdown-item command="message">
                  <i class="el-icon-chat-dot-square"></i> 批量消息提醒
                </el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </div>
          
          <div class="stats-section">
            <div class="stat-item">
              <span class="stat-label">应提交学生：</span>
              <span class="stat-value">{{ totalRequiredStudents }}</span>
            </div>
            <div class="stat-item">
              <span class="stat-label">已提交：</span>
              <span class="stat-value submitted">{{ submittedCount }}</span>
            </div>
            <div class="stat-item">
              <span class="stat-label">未提交：</span>
              <span class="stat-value pending">{{ pendingCount }}</span>
            </div>
            <div class="stat-item">
              <span class="stat-label">提交率：</span>
              <span class="stat-value rate">{{ submissionRate }}%</span>
            </div>
          </div>
        </div>
      </el-card>

      <!-- 学生月报状态列表 -->
      <el-card shadow="hover" class="reports-card">
        <div slot="header" class="card-header">
          <span><i class="el-icon-user"></i> 学生月报状态</span>
          <div>
            <el-input
              v-model="searchKeyword"
              placeholder="搜索学生姓名或学号"
              size="small"
              style="width: 200px; margin-right: 10px;"
              prefix-icon="el-icon-search"
              clearable>
            </el-input>
          </div>
        </div>
        
        <!-- 桌面端表格 -->
        <el-table 
          v-if="!isMobile"
          :data="filteredReports" 
          v-loading="loadingReports" 
          stripe 
          size="small"
          style="width: 100%">
          
          <el-table-column label="学生信息" width="180">
            <template slot-scope="scope">
              <div class="student-info-cell">
                <div class="student-name">{{ scope.row.name }}</div>
                <div class="student-number">{{ scope.row.student_number }}</div>
                <div class="student-class">{{ scope.row.class_id }}</div>
              </div>
            </template>
          </el-table-column>
          
          <el-table-column label="是否需要提交" width="120" align="center">
            <template slot-scope="scope">
              <el-tag :type="scope.row.is_required ? 'warning' : 'info'" size="small">
                {{ scope.row.is_required ? '需要提交' : '无需提交' }}
              </el-tag>
            </template>
          </el-table-column>
          
          <el-table-column label="提交状态" width="120" align="center">
            <template slot-scope="scope">
              <el-tag 
                :type="getStatusType(scope.row.submission_status)" 
                size="small">
                {{ getStatusText(scope.row.submission_status) }}
              </el-tag>
            </template>
          </el-table-column>
          
          <el-table-column prop="flight_start_date" label="开飞日期" width="120"></el-table-column>
          <el-table-column prop="flight_end_date" label="停飞日期" width="120"></el-table-column>
          
          <el-table-column label="提交时间" width="160">
            <template slot-scope="scope">
              <span v-if="scope.row.submitted_at">{{ scope.row.submitted_at }}</span>
              <span v-else style="color: #c0c4cc;">未提交</span>
            </template>
          </el-table-column>
          
          <el-table-column label="操作"  align="center">
            <template slot-scope="scope">
              <el-button 
                v-if="scope.row.submission_status === 'submitted'"
                type="primary" 
                size="mini" 
                icon="el-icon-view" 
                @click="viewMonthlyReport(scope.row)">
                查看
              </el-button>
              <el-dropdown 
                v-if="scope.row.submission_status === 'pending' && scope.row.is_required"
                @command="handleReminderCommand"
                :loading="scope.row.isSendingReminder">
                <el-button 
                  type="warning" 
                  size="mini" 
                  icon="el-icon-message"
                  :loading="scope.row.isSendingReminder">
                  发送提醒<i class="el-icon-arrow-down el-icon--right"></i>
                </el-button>
                <el-dropdown-menu slot="dropdown">
                  <el-dropdown-item :command="{type: 'email', student: scope.row}">
                    <i class="el-icon-message"></i> 邮件提醒
                  </el-dropdown-item>
                  <el-dropdown-item :command="{type: 'message', student: scope.row}">
                    <i class="el-icon-chat-dot-square"></i> 消息提醒
                  </el-dropdown-item>
                </el-dropdown-menu>
              </el-dropdown>
              <span v-if="scope.row.submission_status !== 'submitted' && scope.row.submission_status !== 'pending'" style="color: #c0c4cc;">暂无月报</span>
            </template>
          </el-table-column>
        </el-table>
        
        <!-- 移动端列表 -->
        <div v-else class="mobile-reports-list">
          <div 
            v-for="report in filteredReports" 
            :key="report.student_number" 
            class="report-item">
            <div class="report-header">
              <div class="student-info">
                <h4>{{ report.name }}</h4>
                <span class="student-number">{{ report.student_number }}</span>
                <span class="student-class">{{ report.class_id }}</span>
              </div>
              <div class="status-badges">
                <el-tag :type="report.is_required ? 'warning' : 'info'" size="small">
                  {{ report.is_required ? '需要提交' : '无需提交' }}
                </el-tag>
                <el-tag 
                  :type="getStatusType(report.submission_status)" 
                  size="small" 
                  style="margin-left: 5px;">
                  {{ getStatusText(report.submission_status) }}
                </el-tag>
              </div>
            </div>
            
            <div class="report-details">
              <div class="detail-row" v-if="report.flight_start_date">
                <label>开飞日期：</label>
                <span>{{ report.flight_start_date }}</span>
              </div>
              <div class="detail-row" v-if="report.flight_end_date">
                <label>停飞日期：</label>
                <span>{{ report.flight_end_date }}</span>
              </div>
              <div class="detail-row" v-if="report.submitted_at">
                <label>提交时间：</label>
                <span>{{ report.submitted_at }}</span>
              </div>
            </div>
            
            <div class="report-actions">
              <el-button 
                v-if="report.submission_status === 'submitted'"
                type="primary" 
                size="small" 
                icon="el-icon-view" 
                @click="viewMonthlyReport(report)">
                查看月报
              </el-button>
              <el-dropdown 
                v-if="report.submission_status === 'pending' && report.is_required"
                @command="handleReminderCommand"
                :loading="report.isSendingReminder">
                <el-button 
                  type="warning" 
                  size="small" 
                  icon="el-icon-message"
                  :loading="report.isSendingReminder">
                  发送提醒<i class="el-icon-arrow-down el-icon--right"></i>
                </el-button>
                <el-dropdown-menu slot="dropdown">
                  <el-dropdown-item :command="{type: 'email', student: report}">
                    <i class="el-icon-message"></i> 邮件提醒
                  </el-dropdown-item>
                  <el-dropdown-item :command="{type: 'message', student: report}">
                    <i class="el-icon-chat-dot-square"></i> 消息提醒
                  </el-dropdown-item>
                </el-dropdown-menu>
              </el-dropdown>
            </div>
          </div>
        </div>
        
        <!-- 无数据状态 -->
        <div v-if="filteredReports.length === 0 && !loadingReports" class="no-data">
          <i class="el-icon-document"></i>
          <p>{{ selectedMonth ? '该月份暂无月报数据' : '请选择月份查看月报数据' }}</p>
        </div>
      </el-card>
    </div>
    
    <!-- 月报详情对话框 -->
    <el-dialog 
      :title="`${selectedMonthlyReport ? selectedMonthlyReport.student_name : ''} - ${selectedMonthlyReport ? selectedMonthlyReport.report_month_display : ''}`"
      :visible.sync="detailDialogVisible" 
      :width="isMobile ? '95%' : '900px'" 
      :fullscreen="isMobile"
      :before-close="closeDetailDialog"
      class="monthly-report-dialog">
      <div v-if="selectedMonthlyReport" class="monthly-report-detail">
        <!-- 头部概览卡片 -->
        <div class="overview-cards">
          <div class="overview-card highlight">
            <div class="card-icon">
              <i class="el-icon-time"></i>
            </div>
            <div class="card-content">
              <div class="card-title">累计训练时间</div>
              <div class="card-value">{{ selectedMonthlyReport.total_training_display || formatMinutesToDisplay(selectedMonthlyReport.total_training_minutes) || '0小时0分钟' }}</div>
            </div>
          </div>
          <div class="overview-card">
            <div class="card-icon">
              <i class="el-icon-refresh"></i>
            </div>
            <div class="card-content">
              <div class="card-title">累计起落次数</div>
              <div class="card-value">{{ selectedMonthlyReport.total_landings || 0 }} 次</div>
            </div>
          </div>
          <div class="overview-card">
            <div class="card-icon">
              <i class="el-icon-location"></i>
            </div>
            <div class="card-content">
              <div class="card-title">训练基地</div>
              <div class="card-value">{{ selectedMonthlyReport.training_base || '-' }}</div>
            </div>
          </div>
        </div>

        <!-- 标签页内容 -->
        <el-tabs v-model="activeDetailTab" type="card" class="detail-tabs">
          <!-- 本月情况 -->
          <el-tab-pane label="本月情况" name="monthly">
            <div class="tab-content">
              <div class="data-grid">
                <div class="data-item">
                  <span class="data-label">当月飞行时间</span>
                  <span class="data-value primary">{{ selectedMonthlyReport.monthly_flight_display || formatMinutesToDisplay(selectedMonthlyReport.monthly_flight_minutes) || '0小时0分钟' }}</span>
                </div>
                <div class="data-item">
                  <span class="data-label">本月起落次数</span>
                  <span class="data-value">{{ selectedMonthlyReport.monthly_landings || 0 }} 次</span>
                </div>
              </div>
            </div>
          </el-tab-pane>

          <!-- 真机训练 -->
          <el-tab-pane label="真机训练" name="aircraft">
            <div class="tab-content">
              <div class="data-grid">
                <div class="data-item">
                  <span class="data-label">真机总时间</span>
                  <span class="data-value primary">{{ selectedMonthlyReport.total_aircraft_display || formatMinutesToDisplay(selectedMonthlyReport.total_aircraft_minutes) || '0小时0分钟' }}</span>
                </div>
                <div class="data-item">
                  <span class="data-label">机长时间</span>
                  <span class="data-value">{{ selectedMonthlyReport.captain_display || formatMinutesToDisplay(selectedMonthlyReport.captain_minutes) || '0小时0分钟' }}</span>
                </div>
                <div class="data-item">
                  <span class="data-label">带飞时间</span>
                  <span class="data-value">{{ selectedMonthlyReport.instructor_display || formatMinutesToDisplay(selectedMonthlyReport.instructor_minutes) || '0小时0分钟' }}</span>
                </div>
                <div class="data-item">
                  <span class="data-label">单飞时间</span>
                  <span class="data-value">{{ selectedMonthlyReport.solo_display || formatMinutesToDisplay(selectedMonthlyReport.solo_minutes) || '0小时0分钟' }}</span>
                </div>
                <div class="data-item">
                  <span class="data-label">转场时间</span>
                  <span class="data-value">{{ selectedMonthlyReport.cross_country_display || formatMinutesToDisplay(selectedMonthlyReport.cross_country_minutes) || '0小时0分钟' }}</span>
                </div>
                <div class="data-item">
                  <span class="data-label">仪表时间</span>
                  <span class="data-value">{{ selectedMonthlyReport.instrument_display || formatMinutesToDisplay(selectedMonthlyReport.instrument_minutes) || '0小时0分钟' }}</span>
                </div>
                <div class="data-item">
                  <span class="data-label">真机起落次数</span>
                  <span class="data-value">{{ selectedMonthlyReport.total_aircraft_landings || 0 }} 次</span>
                </div>
                <div class="data-item">
                  <span class="data-label">加时小时数</span>
                  <span class="data-value primary">{{ selectedMonthlyReport.overtime_display || formatMinutesToDisplay(selectedMonthlyReport.overtime_minutes) || '0小时0分钟' }}</span>
                </div>
              </div>
            </div>
          </el-tab-pane>

          <!-- 模拟机训练 -->
          <el-tab-pane label="模拟机训练" name="simulator">
            <div class="tab-content">
              <div class="data-grid">
                <div class="data-item">
                  <span class="data-label">模拟机时间</span>
                  <span class="data-value primary">{{ selectedMonthlyReport.simulator_display || formatMinutesToDisplay(selectedMonthlyReport.simulator_minutes) || '0小时0分钟' }}</span>
                </div>
                <div class="data-item">
                  <span class="data-label">模拟机起落次数</span>
                  <span class="data-value">{{ selectedMonthlyReport.simulator_landings || 0 }} 次</span>
                </div>
              </div>
            </div>
          </el-tab-pane>

          <!-- 其他信息 -->
          <el-tab-pane label="其他信息" name="other">
            <div class="tab-content">
              <div class="data-grid">
                <div class="data-item">
                  <span class="data-label">预计回国日期</span>
                  <span class="data-value">{{ selectedMonthlyReport.expected_return_date || '-' }}</span>
                </div>
                <div class="data-item">
                  <span class="data-label">开飞日期</span>
                  <span class="data-value">{{ selectedMonthlyReport.flight_start_date || '-' }}</span>
                </div>
                <div class="data-item">
                  <span class="data-label">停飞日期</span>
                  <span class="data-value">{{ selectedMonthlyReport.suspension_date || '-' }}</span>
                </div>
                <div class="data-item">
                  <span class="data-label">提交时间</span>
                  <span class="data-value">{{ selectedMonthlyReport.submitted_at || '-' }}</span>
                </div>
              </div>
            </div>
          </el-tab-pane>
          
          <!-- 考试相关情况 -->
          <el-tab-pane label="考试相关" name="exams">
            <div class="tab-content">
              <div class="data-grid">
                <div class="data-item">
                  <span class="data-label">理论考试不通过次数</span>
                  <span class="data-value">{{ selectedMonthlyReport.theory_exam_failures || 0 }} 次</span>
                </div>
                <div class="data-item">
                  <span class="data-label">实践考试不通过次数</span>
                  <span class="data-value">{{ selectedMonthlyReport.practical_exam_failures || 0 }} 次</span>
                </div>
              </div>
            </div>
          </el-tab-pane>
        </el-tabs>
      </div>
      
      <span slot="footer" class="dialog-footer">
        <el-button type="primary" @click="closeDetailDialog">关闭</el-button>
      </span>
    </el-dialog>
    
    <!-- 截止日期设置对话框 -->
    <el-dialog
      title="设置截止日期"
      :visible.sync="emailSettings.showDeadlineDialog"
      width="400px"
      center>
      <div class="deadline-form">
        <p>请设置月报提交截止日期，将在邮件中提醒学生</p>
        <el-date-picker
          v-model="emailSettings.deadline"
          type="date"
          placeholder="选择截止日期"
          format="yyyy年MM月dd日"
          value-format="yyyy年MM月dd日"
          style="width: 100%">
        </el-date-picker>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="emailSettings.showDeadlineDialog = false">取消</el-button>
        <el-button type="primary" @click="setDeadline">确定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import api from "../../api/index"
import axiosInstance from "../../api/axiosInstance"

export default {
  name: 'MonthlyReportView',
  data() {
    return {
      loading: true,
      isLeader: false,
      batchInfo: {},
      loadingReports: false,
      selectedYear: '',
      selectedMonth: '',
      searchKeyword: '',
      monthlyReports: [],
      detailDialogVisible: false,
      selectedMonthlyReport: null,
      activeDetailTab: 'monthly',
      isMobile: false,
      isSendingEmails: false,
      isSendingReminders: false,
      emailSettings: {
        deadline: '',
        showDeadlineDialog: false
      },
      pickerOptions: {
        disabledDate(time) {
          // 禁用未来的月份
          return time.getTime() > Date.now()
        }
      }
    }
  },
  
  computed: {
    filteredReports() {
      if (!this.searchKeyword) {
        return this.monthlyReports
      }
      const keyword = this.searchKeyword.toLowerCase()
      return this.monthlyReports.filter(report => 
        report.name.toLowerCase().includes(keyword) || 
        report.student_number.toLowerCase().includes(keyword)
      )
    },
    
    totalRequiredStudents() {
      return this.monthlyReports.filter(r => r.is_required).length
    },
    
    submittedCount() {
      return this.monthlyReports.filter(r => r.is_required && r.submission_status === 'submitted').length
    },
    
    pendingCount() {
      return this.monthlyReports.filter(r => r.is_required && r.submission_status === 'pending').length
    },
    
    submissionRate() {
      if (this.totalRequiredStudents === 0) return 0
      return Math.round((this.submittedCount / this.totalRequiredStudents) * 100)
    }
  },
  
  async mounted() {
    this.checkMobile()
    window.addEventListener('resize', this.checkMobile)
    await this.initializePage()
  },
  
  beforeDestroy() {
    window.removeEventListener('resize', this.checkMobile)
  },
  
  methods: {
    // 检测移动端
    checkMobile() {
      this.isMobile = window.innerWidth <= 768
    },
    
    // 将分钟转换为x小时x分钟显示格式
    formatMinutesToDisplay(minutes) {
      if (!minutes) return "0小时0分钟"
      const hours = Math.floor(minutes / 60)
      const mins = minutes % 60
      return `${hours}小时${mins}分钟`
    },
    
    async initializePage() {
      try {
        this.loading = true
        await this.checkPermission()
        if (this.isLeader) {
          // 设置默认为当前月份
          const now = new Date()
          this.selectedYear = now.getFullYear()
          this.selectedMonth = (now.getMonth()).toString().padStart(2, '0')
          if (this.selectedMonth == '00') {
            this.selectedYear = now.getFullYear() - 1
            this.selectedMonth = '12'
          }
          this.selectedMonth = `${this.selectedYear}-${this.selectedMonth}`
          await this.loadMonthlyReports()
        }
      } catch (error) {
        this.$message.error('页面初始化失败: ' + error.message)
      } finally {
        this.loading = false
      }
    },
    
    async checkPermission() {
      try {
        const response = await axiosInstance.get(api.checkLeaderPermission)
        if (response.data.status === 'success') {
          this.isLeader = true
          this.batchInfo = response.data.data
        } else {
          this.isLeader = false
        }
      } catch (error) {
        this.isLeader = false
        throw error
      }
    },
    
    async loadMonthlyReports() {
      if (!this.selectedMonth) {
        this.monthlyReports = []
        return
      }
      
      try {
        this.loadingReports = true
        const params = { month: this.selectedMonth }
        const response = await axiosInstance.get(api.getBatchMonthlyReportsStatus, { params })
        
        if (response.data.status === 'success') {
          this.monthlyReports = response.data.data || []
        } else {
          this.$message.error(response.data.message || '加载月报数据失败')
          this.monthlyReports = []
        }
      } catch (error) {
        this.$message.error('加载月报数据失败: ' + error.message)
        this.monthlyReports = []
      } finally {
        this.loadingReports = false
      }
    },
    
    async viewMonthlyReport(reportInfo) {
      try {
        const params = { 
          student_number: reportInfo.student_number,
          month: this.selectedMonth 
        }
        const response = await axiosInstance.get(api.getBatchStudentMonthlyReport, { params })
        
        if (response.data.status === 'success') {
          this.selectedMonthlyReport = response.data.data
          this.detailDialogVisible = true
        } else {
          this.$message.error(response.data.message || '获取月报详情失败')
        }
      } catch (error) {
        this.$message.error('获取月报详情失败: ' + error.message)
      }
    },
    
    closeDetailDialog() {
      this.detailDialogVisible = false
      this.selectedMonthlyReport = null
    },
    
    getStatusType(status) {
      switch(status) {
        case 'submitted': return 'success'
        case 'pending': return 'warning'
        case 'not_required': return 'info'
        default: return ''
      }
    },
    
    getStatusText(status) {
      switch(status) {
        case 'submitted': return '已提交'
        case 'pending': return '未提交'
        case 'not_required': return '无需提交'
        default: return '未知'
      }
    },
    
    goBack() {
      this.$router.go(-1)
    },
    
    // 处理提醒命令
    handleReminderCommand(command) {
      if (command.type === 'email') {
        this.sendEmailReminder(command.student)
      } else if (command.type === 'message') {
        this.sendMessageReminder(command.student)
      }
    },
    
    // 处理批量提醒命令
    handleBatchReminderCommand(command) {
      if (command === 'email') {
        this.sendBatchEmailReminders()
      } else if (command === 'message') {
        this.sendBatchMessageReminders()
      }
    },
    
    // 发送单个邮件提醒
    async sendEmailReminder(student) {
      try {
        if (!this.emailSettings.deadline) {
          this.emailSettings.showDeadlineDialog = true;
          return;
        }
        
        // 设置当前学生的发送状态
        this.$set(student, 'isSendingReminder', true);
        
        const email = `zy2106239@buaa.edu.cn`;
        const monthDisplay = this.selectedMonth.replace('-', '年') + '月';
        
        const params = {
          email: email,
          subject: `${monthDisplay}月报填写提醒`,
          content: `亲爱的${student.name}同学：\n\n请及时填写${monthDisplay}月报内容。截止时间为${this.emailSettings.deadline}。\n\n北航飞行学院`,
          student_number: student.student_number,
          month: this.selectedMonth
        };
        
        const response = await axiosInstance.post(api.sendEmailReminder, params);
        
        if (response.data.status === 'success') {
          this.$message.success(`已成功发送提醒邮件至 ${email}`);
        } else {
          this.$message.error(response.data.message || '发送邮件失败');
        }
      } catch (error) {
        this.$message.error('发送邮件失败: ' + error.message);
      } finally {
        this.$set(student, 'isSendingReminder', false);
      }
    },
    
    // 批量发送邮件提醒
    async sendBatchEmailReminders() {
      if (!this.emailSettings.deadline) {
        this.emailSettings.showDeadlineDialog = true;
        return;
      }
      
      try {
        this.isSendingReminders = true;
        
        const pendingStudents = this.monthlyReports.filter(
          r => r.is_required && r.submission_status === 'pending'
        );
        
        if (pendingStudents.length === 0) {
          this.$message.info('没有需要提醒的学生');
          return;
        }
        
        const monthDisplay = this.selectedMonth.replace('-', '年') + '月';
        const emails = pendingStudents.map(s => `${s.student_number}@buaa.edu.cn`);
        const studentNumbers = pendingStudents.map(s => s.student_number);
        
        const params = {
          emails: emails,
          subject: `${monthDisplay}月报填写提醒`,
          content: `亲爱的同学：\n\n请及时填写${monthDisplay}月报内容。截止时间为${this.emailSettings.deadline}。\n\n北航飞行学院`,
          student_numbers: studentNumbers,
          month: this.selectedMonth
        };
        
        const response = await axiosInstance.post(api.sendBatchEmailReminders, params);
        
        if (response.data.status === 'success') {
          this.$message.success(`已成功发送${pendingStudents.length}封提醒邮件`);
        } else {
          this.$message.error(response.data.message || '批量发送邮件失败');
        }
      } catch (error) {
        this.$message.error('批量发送邮件失败: ' + error.message);
      } finally {
        this.isSendingReminders = false;
      }
    },
    
    // 设置截止日期
    setDeadline() {
      if (!this.emailSettings.deadline) {
        this.$message.warning('请设置截止日期');
        return;
      }
      
      this.emailSettings.showDeadlineDialog = false;
      
      // 根据调用来源执行相应的邮件发送操作
      if (this.emailSettings.currentStudent) {
        this.sendEmailReminder(this.emailSettings.currentStudent);
        this.emailSettings.currentStudent = null;
      } else {
        this.sendBatchEmailReminders();
      }
    },
    
    // 发送单个消息提醒
    async sendMessageReminder(student) {
      try {
        // 设置当前学生的发送状态
        this.$set(student, 'isSendingReminder', true);
        
        const [year, month] = this.selectedMonth.split('-');
        
        const params = {
          student_number: student.student_number,
          year: parseInt(year),
          month: parseInt(month)
        };
        
        const response = await axiosInstance.post(api.sendMessageReminder, params);
        
        if (response.data.status === 'success') {
          this.$message.success(`已成功发送消息提醒给 ${student.name}`);
        } else {
          this.$message.error(response.data.message || '发送消息失败');
        }
      } catch (error) {
        this.$message.error('发送消息失败: ' + error.message);
      } finally {
        this.$set(student, 'isSendingReminder', false);
      }
    },
    
    // 批量发送消息提醒
    async sendBatchMessageReminders() {
      try {
        this.isSendingReminders = true;
        
        const pendingStudents = this.monthlyReports.filter(
          r => r.is_required && r.submission_status === 'pending'
        );
        
        if (pendingStudents.length === 0) {
          this.$message.info('没有需要提醒的学生');
          return;
        }
        
        const [year, month] = this.selectedMonth.split('-');
        
        const params = {
          year: parseInt(year),
          month: parseInt(month)
        };
        
        const response = await axiosInstance.post(api.sendBatchMessageReminders, params);
        
        if (response.data.status === 'success') {
          const data = response.data.data;
          this.$message.success(`批量消息提醒发送完成：成功 ${data.sent_count} 人，失败 ${data.failed_count} 人`);
        } else {
          this.$message.error(response.data.message || '批量发送消息失败');
        }
      } catch (error) {
        this.$message.error('批量发送消息失败: ' + error.message);
      } finally {
        this.isSendingReminders = false;
      }
    }
  }
}
</script>

<style scoped>
/* 基础布局 */
.monthly-report-view-container { 
  padding: 10px; 
  min-height: 100vh; 
  background-color: #f5f7fa; 
}

.loading-container { 
  display: flex; 
  flex-direction: column; 
  align-items: center; 
  justify-content: center; 
  height: 300px; 
  color: #909399; 
}
.loading-container i { font-size: 32px; margin-bottom: 15px; }

.no-permission { 
  text-align: center; 
  padding: 60px 20px; 
  color: #606266; 
}
.no-permission i { font-size: 48px; color: #f56c6c; margin-bottom: 15px; }
.no-permission h3 { margin: 15px 0; color: #303133; }

/* 页面头部 */
.page-header { 
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px; 
  padding: 15px; 
  background: white; 
  border-radius: 8px; 
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1); 
}
.header-content h2 { 
  margin: 0 0 8px 0; 
  color: #303133; 
  display: flex; 
  align-items: center; 
  gap: 8px;
  font-size: 18px;
}
.batch-info { 
  margin: 0; 
  color: #909399; 
  font-size: 13px; 
}

/* 筛选卡片 */
.filter-card {
  margin-bottom: 15px;
}
.filter-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 15px;
}
.filter-controls {
  display: flex;
  gap: 10px;
  align-items: center;
}
.stats-section {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
}
.stat-item {
  display: flex;
  align-items: center;
  gap: 5px;
}
.stat-label {
  font-size: 14px;
  color: #606266;
}
.stat-value {
  font-weight: 600;
  font-size: 16px;
}
.stat-value.submitted {
  color: #67c23a;
}
.stat-value.pending {
  color: #e6a23c;
}
.stat-value.rate {
  color: #409eff;
}

/* 报告卡片 */
.reports-card {
  min-height: 400px;
}
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

/* 学生信息单元格 */
.student-info-cell {
  padding: 5px 0;
}
.student-name {
  font-weight: bold;
  color: #303133;
  font-size: 14px;
  margin-bottom: 2px;
}
.student-number {
  color: #909399;
  font-size: 12px;
  margin-bottom: 1px;
}
.student-class {
  color: #c0c4cc;
  font-size: 11px;
}

/* 移动端列表 */
.mobile-reports-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.report-item {
  padding: 15px;
  background: #f9f9f9;
  border-radius: 8px;
  border-left: 4px solid #409eff;
}
.report-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 10px;
}
.student-info h4 {
  margin: 0 0 5px 0;
  font-size: 16px;
  color: #303133;
}
.student-info .student-number {
  margin-right: 10px;
  font-size: 12px;
  color: #909399;
}
.student-info .student-class {
  font-size: 11px;
  color: #c0c4cc;
}
.status-badges {
  display: flex;
  flex-direction: column;
  gap: 5px;
}
.report-details {
  margin-bottom: 10px;
}
.detail-row {
  display: flex;
  margin-bottom: 5px;
  font-size: 13px;
}
.detail-row label {
  color: #606266;
  width: 80px;
  flex-shrink: 0;
}
.report-actions {
  text-align: right;
}

/* 无数据状态 */
.no-data {
  text-align: center;
  padding: 60px 20px;
  color: #909399;
}
.no-data i {
  font-size: 48px;
  margin-bottom: 15px;
  color: #c0c4cc;
}

/* 月报详情弹窗 */
.monthly-report-dialog .el-dialog {
  border-radius: 12px;
  overflow: hidden;
}

.monthly-report-dialog .el-dialog__header {
  background: linear-gradient(135deg, #409eff 0%, #67c23a 100%);
  color: white;
  padding: 20px 24px;
}

.monthly-report-dialog .el-dialog__title {
  color: white;
  font-weight: 600;
  font-size: 18px;
}

.monthly-report-dialog .el-dialog__body {
  padding: 0;
  max-height: 70vh;
  overflow-y: auto;
}

/* 概览卡片 */
.overview-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 16px;
  padding: 24px;
  background: #f8f9fa;
  border-bottom: 1px solid #e4e7ed;
}

.overview-card {
  display: flex;
  align-items: center;
  padding: 20px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.overview-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
}

.overview-card.highlight {
  background: linear-gradient(135deg, #409eff 0%, #67c23a 100%);
  color: white;
}

.overview-card.highlight .card-title,
.overview-card.highlight .card-value {
  color: white;
}

.card-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 16px;
  background: rgba(64, 158, 255, 0.1);
  flex-shrink: 0;
}

.overview-card.highlight .card-icon {
  background: rgba(255, 255, 255, 0.2);
}

.card-icon i {
  font-size: 24px;
  color: #409eff;
}

.overview-card.highlight .card-icon i {
  color: white;
}

.card-content {
  flex: 1;
  min-width: 0;
}

.card-title {
  font-size: 14px;
  color: #606266;
  margin-bottom: 8px;
  font-weight: 500;
}

.card-value {
  font-size: 18px;
  color: #303133;
  font-weight: 600;
  line-height: 1.2;
}

/* 详情标签页 */
.detail-tabs {
  margin: 0;
}

.detail-tabs .el-tabs__header {
  margin: 0;
  background: white;
  padding: 0 24px;
  border-bottom: 2px solid #f0f2f5;
}

.detail-tabs .el-tabs__nav-wrap {
  padding: 16px 0;
}

.detail-tabs .el-tabs__item {
  font-weight: 600;
  color: #606266;
  border-radius: 8px 8px 0 0;
  margin-right: 8px;
}

.detail-tabs .el-tabs__item.is-active {
  color: #409eff;
  background: #f0f9ff;
}

/* 标签页内容 */
.tab-content {
  padding: 24px;
  min-height: 200px;
}

.data-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 16px;
}

.data-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: #f8f9fa;
  border-radius: 8px;
  border-left: 4px solid #e4e7ed;
  transition: all 0.3s ease;
}

.data-item:hover {
  background: #f0f9ff;
  border-left-color: #409eff;
  transform: translateX(4px);
}

.data-label {
  font-size: 14px;
  color: #606266;
  font-weight: 500;
  flex-shrink: 0;
  margin-right: 16px;
}

.data-value {
  font-size: 15px;
  color: #303133;
  font-weight: 600;
  text-align: right;
}

.data-value.primary {
  color: #409eff;
  font-size: 16px;
}

/* 对话框底部 */
.monthly-report-dialog .el-dialog__footer {
  padding: 16px 24px;
  background: #f8f9fa;
  border-top: 1px solid #e4e7ed;
}

.monthly-report-dialog .dialog-footer .el-button {
  padding: 10px 24px;
  font-weight: 600;
}

/* 移动端适配 */
@media (max-width: 768px) {
  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
  .header-actions {
    width: 100%;
  }
  .filter-section {
    flex-direction: column;
    align-items: flex-start;
  }
  .stats-section {
    width: 100%;
    justify-content: space-between;
  }
  
  /* 弹窗移动端适配 */
  .overview-cards {
    grid-template-columns: 1fr;
    padding: 16px;
    gap: 12px;
  }
  
  .overview-card {
    padding: 16px;
  }
  
  .card-icon {
    width: 40px;
    height: 40px;
    margin-right: 12px;
  }
  
  .card-icon i {
    font-size: 20px;
  }
  
  .card-title {
    font-size: 13px;
  }
  
  .card-value {
    font-size: 16px;
  }
  
  .detail-tabs .el-tabs__header {
    padding: 0 16px;
  }
  
  .tab-content {
    padding: 16px;
  }
  
  .data-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }
  
  .data-item {
    padding: 12px 16px;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
  
  .data-label {
    margin-right: 0;
    font-size: 13px;
  }
  
  .data-value {
    text-align: left;
    font-size: 14px;
  }
  
  .data-value.primary {
    font-size: 15px;
  }
  
  .monthly-report-dialog .el-dialog__header {
    padding: 16px 20px;
  }
  
  .monthly-report-dialog .el-dialog__title {
    font-size: 16px;
  }
}
</style>